import type { ComponentProps, ReactNode } from 'react';
import { Button, Tooltip } from '@customerio/pluma-components/react';

/**
 * What an editable cell looks like when nobody is editing it.
 *
 * Always the same button, disabled when it cannot be used, so that the
 * explanation for why has something focusable to hang on and the cell does
 * not change shape depending on who is looking at it.
 */

export type EditTriggerProps = {
  /** False for a reserved row, or for a viewer without the permission. */
  canEdit: boolean;
  /** Whether the viewer would be allowed if the row were not reserved. */
  hasPermission: boolean;
  /** Why a viewer without the permission cannot use it. */
  tooltip: string;
  onStart: () => void;
  children: ReactNode;
};

export function EditTrigger({ canEdit, hasPermission, tooltip, onStart, children }: EditTriggerProps) {
  const trigger = (extraProps: Partial<ComponentProps<typeof Button>> = {}) => (
    <Button
      variant="tertiary"
      size="sm"
      // Bounded, so there is something to truncate against: a long
      // description would otherwise stretch its column.
      maxW="full"
      icon={canEdit ? 'edit' : undefined}
      iconPosition="trailing"
      isDisabled={!canEdit}
      onClick={onStart}
      {...extraProps}
    >
      {children}
    </Button>
  );

  // A reserved row is nobody's to edit, so there is nothing to explain; only
  // a viewer who would otherwise be allowed is told why.
  if (canEdit || hasPermission) {
    return trigger();
  }

  // The tooltip attaches to the button rather than wrapping it: a default
  // Tooltip renders a button of its own, which cannot nest inside one.
  return (
    <Tooltip isCustom={true} content={tooltip}>
      {(context) => trigger({ ref: context.reference.setReference, ...context.reference.getReferenceProps() })}
    </Tooltip>
  );
}
